import { Tab, Tabs, Grid, Paper, Button } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TabPanel } from '../../../components/tab-panel';
import { ServicesBundleTab } from './services-bundle-tab/services-tab';
import { WorkOrdersTab } from './workorders-tab/work-orders-tab';
import { useSaveData } from '../../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import { Unit } from '../../../models/unit';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { unitsApiKeyStore } from '../../../api/units-api-key-store';
import { PlanTab } from '../unit-details/plan-tab/plan-tabs';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';
import { ServicesTab } from './services-tab/services-tab';
import { CustomerDetailsTab } from './unit-contacts-tab/customer-contacts-tab';
import { FilesTab } from './file-tab/files.tab';
import { InspectionReportsTab } from '../unit-details/inspection-report-tab/inspection-report.tab';

interface DetailsTabsProps {
  unit: Unit;
  permissionForTab: any;
  refetchUnit: () => void;
}

export const DetailsTabs = ({ unit, permissionForTab, refetchUnit }: DetailsTabsProps) => {
  const { unitID } = useParams();
  const [value, setValue] = useState(unit.workOrderCount > 0 ? 'workOrders' : 'services');
  const {
    isLoading: isGenerating,
    data,
    mutateAsync,
  } = useSaveData<any>(workOrdersApiKeyStore.generateWorkOrder(), () => unitsApiKeyStore.getUnitDetails(unitID));

  const handleTabsChange = (event: any, value: any) => {
    setValue(value);
  };

  const generateWorkOrder = async () => {
    await mutateAsync({ data: { unitID: unitID } });
    setValue('workOrders');
  };

  const availableTabs = useMemo(
    () =>
      [
        permissionForTab?.serviceTab && 'services',

        permissionForTab?.workorders && 'workOrders',
        permissionForTab?.serviceBundleTab && 'services-bundle',
        permissionForTab?.fileTab && 'files',
        permissionForTab?.planTab && 'plans',
        permissionForTab?.inspectionReportTab && 'inspectionReports',
      ].filter(Boolean),
    [permissionForTab]
  );

  useEffect(() => {
    if (!availableTabs.includes(value)) {
      setValue(availableTabs[0] as string);
    }
  }, [availableTabs]);

  if (!unitID) {
    return <div>Invalid Unit</div>;
  }

  const isPlanEnabled = process.env.REACT_APP_PLAN_FLAG === 'true';
  const isBundlePermission = useAuthorize([UserPermissionName?.VIEW_SERVICE_BUNDLE]);
  return (
    <Paper elevation={0}>
      <Grid item>
        <LoadingIndicator isLoading={isGenerating} />
        <Tabs value={value} onChange={handleTabsChange}>
          <Tab value="workOrders" label="Work Orders" hidden={!permissionForTab?.workorders} />
          <Tab value="services" label="Services" hidden={!permissionForTab?.serviceTab} />
          <Tab value="services-bundle" label="Service Bundles" hidden={!permissionForTab?.serviceBundleTab} />
          <Tab value="contacts" label="Contacts" hidden={!permissionForTab?.contactTab} />
          <Tab value="files" label="Files" hidden={!permissionForTab?.fileTab} />
          <Tab value="plans" label="Plans" hidden={!isPlanEnabled || !permissionForTab?.planTab} />
          <Tab value="inspectionReports" label="Inspection Reports" hidden={!permissionForTab?.inspectionReportTab} />
          <div className="d-flex justify-content-end" style={{ width: '100%', padding: '8px' }}>
            {unit?.hasPendingWorkOrderGenerations && (
              <Button
                className="btn btn-link"
                variant="outlined"
                onClick={generateWorkOrder}
                style={{ marginLeft: 876 }}
                disabled={isGenerating || !!data}
              >
                Generate Work Order
              </Button>
            )}
          </div>
        </Tabs>
        {value === 'workOrders' && permissionForTab?.workorders && (
          <TabPanel selectedValue={value} value="workOrders">
            <WorkOrdersTab unit={unit} refetchUnit={refetchUnit} />
          </TabPanel>
        )}
        {value === 'services-bundle' && permissionForTab?.serviceBundleTab && (
          <TabPanel selectedValue={value} value="services-bundle">
            <ServicesBundleTab isCompleted={unit?.isCompleted} unitID={Number(unitID)} />
          </TabPanel>
        )}
        {value === 'services' && permissionForTab?.serviceTab && (
          <TabPanel selectedValue={value} value="services">
            <ServicesTab unitID={Number(unitID)} />
          </TabPanel>
        )}
        {value === 'contacts' && permissionForTab?.contactTab && (
          <TabPanel selectedValue={value} value="contacts">
            <CustomerDetailsTab unitID={Number(unitID)} />
          </TabPanel>
        )}
        {value === 'files' && permissionForTab?.fileTab && (
          <TabPanel selectedValue={value} value="files">
            <FilesTab unitID={Number(unitID)} />
          </TabPanel>
        )}
        {value === 'plans' && permissionForTab?.planTab && (
          <TabPanel selectedValue={value} value="plans">
            <PlanTab
              planRequestArgs={{
                customerID: unit?.customerID,
                officeID: unit?.officeID,
                projectID: unit?.projectID,
                unitID: unit?.unitID,
              }}
            />
          </TabPanel>
        )}
        {value === 'inspectionReports' && permissionForTab?.inspectionReportTab && (
          <TabPanel selectedValue={value} value="inspectionReports">
            <InspectionReportsTab unitID={Number(unitID)} customerName={unit?.customerName} />
          </TabPanel>
        )}
      </Grid>
    </Paper>
  );
};
